// C25 (capture tronquée du 24-09, spec §5.2.4) : une prise adoptée depuis les
// segments restés sur le téléphone COMPLÈTE la capture existante au lieu d'en
// créer une seconde. Le choix vient de l'accueil (« Sauvegarder
// l'enregistrement » sur une carte « complete ») et vit dans
// `take-overrides.ts` ; ces dépendances le font respecter par l'exécuteur :
//
// - fusion réussie et complète : l'audio du serveur est ÉCRASÉ au même
//   emplacement (TUS, `x-upsert: true`), puis `duration_s` corrigée — c'est la
//   confirmation d'envoi qui s'en charge, donc la prise n'est libérée qu'une
//   fois le fichier ET la durée en place (I1) ;
// - fusion en échec ou partielle : dernier recours serveur en mode
//   « complete » (la ligne existante repasse en `assembling`, le serveur
//   écrase l'audio et remet `captured`).
//
// Aucun accès à Capacitor ici : `take-runtime.ts` câble ces fabriques.
import type { SupabaseClient } from "@supabase/supabase-js";
import type { TakeRecorder } from "../native/take-recorder";
import { buildResumableEndpoint } from "./resumable-endpoint";
import type { ResumableUploadFn } from "./resumable-upload";
import type { HandOffOptions, HandOffResult } from "./segment-upload";
import type { TakeOverride, TakeOverrideStore } from "./take-overrides";
import type { RunnerDeps } from "./take-runner";
import type { TakeManifest, TakeMeta } from "./take-types";
import { AUDIO_MIME, BUCKET, buildAudioStoragePath, type ReadAudioFn } from "./upload";

type CompletingOverride = TakeOverride & { complete: NonNullable<TakeOverride["complete"]> };

async function completionOf(
  overrides: Pick<TakeOverrideStore, "get">,
  m: TakeManifest,
): Promise<CompletingOverride | null> {
  const o = await overrides.get(m.takeId);
  return o?.complete ? (o as CompletingOverride) : null;
}

/** La prise complète-t-elle une capture existante (C25) ? */
export async function takeCompletesExistingCapture(
  overrides: Pick<TakeOverrideStore, "get">,
  m: TakeManifest,
): Promise<boolean> {
  return (await completionOf(overrides, m)) !== null;
}

export interface CaptureAudioOverwriter {
  /** Envoie le fichier fusionné PAR-DESSUS l'audio de la capture existante. */
  upload: (m: TakeManifest, o: CompletingOverride) => Promise<boolean>;
  /** Inscrit sur la ligne la durée mesurée du fichier fusionné et son emplacement. */
  record: (m: TakeManifest, o: CompletingOverride) => Promise<boolean>;
}

export interface CaptureAudioOverwriterDeps {
  client: Pick<SupabaseClient, "auth" | "from">;
  recorder: Pick<TakeRecorder, "getTakeSegments">;
  readFile: ReadAudioFn;
  resumableUpload: ResumableUploadFn;
  supabaseUrl: string;
  anonKey: string;
}

export function createCaptureAudioOverwriter(
  o: CaptureAudioOverwriterDeps,
): CaptureAudioOverwriter {
  const pathOf = (c: CompletingOverride) =>
    buildAudioStoragePath(c.meta.userId, c.complete.captureId);
  return {
    upload: async (m, c) => {
      try {
        const files = await o.recorder.getTakeSegments(m.takeId);
        if (!files.merged) return false;
        const { data } = await o.client.auth.getSession();
        const session = data.session;
        if (!session) return false;
        const blob = await o.readFile(files.merged.uri);
        // `defaultResumableUpload` envoie `x-upsert: true` : l'objet existant
        // (l'audio tronqué) est remplacé, jamais un 409.
        const r = await o.resumableUpload({
          endpoint: buildResumableEndpoint(o.supabaseUrl),
          bucket: BUCKET,
          objectPath: pathOf(c),
          blob,
          accessToken: session.access_token,
          anonKey: o.anonKey,
          contentType: AUDIO_MIME,
        });
        return r.ok;
      } catch (err) {
        console.warn(`[take-completion] prise ${m.takeId} : audio non envoyé`, err);
        return false;
      }
    },
    record: async (m, c) => {
      if (!m.merged) return false;
      try {
        const { data, error } = await o.client
          .from("captures")
          .update({
            duration_s: Math.max(0, Math.floor(m.merged.durationMs / 1000)),
            // `keep_audio_coherent` : les deux ensemble (l'audio d'une capture
            // de plus de 14 jours a pu être purgé, `audio_path` nul).
            audio_path: pathOf(c),
            keep_audio: true,
          })
          .eq("id", c.complete.captureId)
          .select("id");
        return !error && Array.isArray(data) && data.length > 0;
      } catch (err) {
        console.warn(`[take-completion] prise ${m.takeId} : durée non inscrite`, err);
        return false;
      }
    },
  };
}

/**
 * `confirmUploaded` de production. Prise ordinaire : la confirmation telle
 * quelle. Prise qui complète une capture : l'objet Storage n'a pas encore la
 * taille du fichier fusionné ⇒ écrasement ; puis la durée inscrite ; puis la
 * même confirmation — la libération ne suit que si les trois ont réussi.
 */
export function createCompletingConfirmer(o: {
  confirm: RunnerDeps["confirmUploaded"];
  overwriter: CaptureAudioOverwriter;
  overrides: Pick<TakeOverrideStore, "get">;
}): RunnerDeps["confirmUploaded"] {
  return async (m) => {
    const c = await completionOf(o.overrides, m);
    if (!c) return o.confirm(m);
    if (!(await o.confirm(m)) && !(await o.overwriter.upload(m, c))) return false;
    if (!(await o.overwriter.record(m, c))) return false;
    return o.confirm(m);
  };
}

/** `handOff` de production : mode « complete » pour une prise qui complète une capture (C25). */
export function createCompletingHandOff(o: {
  handOff: (
    m: TakeManifest,
    meta: TakeMeta,
    opts: Required<HandOffOptions>,
  ) => Promise<HandOffResult>;
  overrides: Pick<TakeOverrideStore, "get">;
}): RunnerDeps["handOff"] {
  return async (m, meta) =>
    o.handOff(m, meta, {
      mode: (await completionOf(o.overrides, m)) ? "complete" : "new",
    });
}
